import { useEffect, useState } from 'react';

import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  Typography,
  useTheme,
} from '@mui/material';
import { red } from '@mui/material/colors';
import { APIEndpoint } from 'common/enum';
import { staffGroupInterface } from 'common/interface';
import { universalPostRequest } from 'common/requestHandler';
import CustomModal from 'components/CustomModal';
import HeaderComp from 'components/elements/HeadingComponent';
import SelectableList from 'components/ListItemsWithSearch';
import useSelectableItems from 'hooks/useSelectableList';
import { popupNotification } from 'notifications/ToastNotification';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'redux/store';

import { getStaffGroups } from './utils';

const DeleteStaffGroup = () => {
  const [staffGroups, setStaffGroups] = useState([] as staffGroupInterface[]);
  const [selectedStaffGroup, setSelectedStaffGroup] = useState(
    {} as staffGroupInterface
  );
  const [staffGroupMembers, setStaffGroupMembers] = useState([] as string[]);
  const { selectedItems, toggleItem, resetSelection } = useSelectableItems();
  const [loading, setLoading] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { companyId } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const theme = useTheme();

  const handleBackClick = () => {
    navigate(-1);
  };

  useEffect(() => {
    getStaffGroups(companyId, setStaffGroups, setLoading);
  }, []);
  useEffect(() => {
    const filteredGroup = staffGroups.filter(
      (group) => group.companyStaffGroupName == selectedItems[0]
    );
    setSelectedStaffGroup(filteredGroup[0]);
    setStaffGroupMembers(filteredGroup.flatMap((group) => group.employeeIds));
  }, [selectedItems]);
  const deleteGroup = async () => {
    setDeletingGroup(true);
    const payload = {
      companyStaffGroupName: selectedStaffGroup.companyStaffGroupName,
      companyId: selectedStaffGroup.companyId,
      actionName: 'DeleteStaffGroup',
    };
    const response = await universalPostRequest(
      APIEndpoint.deleteGroup,
      payload
    );
    setDeletingGroup(false);
    if ('error' in response) {
      popupNotification(response.error, false);
    } else {
      popupNotification(response.message, true);
      getStaffGroups(companyId, setStaffGroups, setLoading);
      setStaffGroupMembers([]);
      setSelectedStaffGroup({} as staffGroupInterface);
    }
  };
  return (
    <>
      <HeaderComp>Delete staff group</HeaderComp>

      {/* Back Button */}
      <Box
        display="flex"
        alignItems="center"
        mb={4}
        sx={{
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateX(-4px)',
          },
        }}
        onClick={handleBackClick}
      >
        <IconButton
          size="small"
          sx={{
            backgroundColor: 'rgba(18, 112, 220, 0.1)',
            color: theme.palette.primary.main,
            mr: 1,
            '&:hover': {
              backgroundColor: 'rgba(18, 112, 220, 0.2)',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="body2"
          color="primary"
          sx={{
            fontWeight: 500,
            userSelect: 'none',
          }}
        >
          Back
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item lg={6}>
          <Typography variant="body1" mb={1} color="textDisabled">
            Staff group members
          </Typography>
          <SelectableList
            items={staffGroups}
            selectedItems={selectedItems}
            toggleItem={toggleItem}
            searchVisible={true}
            showSelectHighlight
            showCheckBoxIcon={false}
            allowSingleSelection
            loading={loading}
            idKey="companyStaffGroupName" // Nested key
            labelKey="companyStaffGroupName" // Nested label
            height={150}
          />
        </Grid>
        <Grid item lg={6}>
          <Typography variant="body1" mb={1} color="textDisabled">
            Staff group members
          </Typography>
          <Box
            height="100%"
            sx={{
              borderRadius: 4,
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          >
            <List>
              {staffGroupMembers.map((member, index) => (
                <>
                  <ListItem key={member}>{member}</ListItem>
                  {index !== staffGroupMembers.length - 1 && <Divider />}
                </>
              ))}
              {!staffGroupMembers.length && (
                <Typography variant="body1" textAlign="center" color={red[400]}>
                  No members
                </Typography>
              )}
            </List>
          </Box>
        </Grid>
        <Grid lg={12}>
          <Box display="flex" justifyContent="flex-end" mt={8}>
            <Button
              variant="contained"
              color="error"
              disabled={selectedItems.length === 0}
              onClick={() => setShowModal(true)}
            >
              Delete group
            </Button>
          </Box>
        </Grid>
      </Grid>
      <CustomModal
        type="error"
        open={showModal}
        onClose={() => setShowModal(false)}
        primaryButtonAction={deleteGroup}
        primaryButtonLabel="Delete group"
        secondaryButtonAction={() => setShowModal(false)}
        secondaryButtonLabel="Cancel"
        primaryButtonLoading={deletingGroup}
        title={`Delete group: ${selectedItems[0]}`}
      >
        <Typography variant="body1">
          Staff group {selectedItems[0]} will be
          <Typography
            variant="body1"
            fontWeight="bold"
            sx={{ color: red[400] }}
            component="span"
          >
            {' '}
            Deleted{' '}
          </Typography>
          along with all its members.
        </Typography>
        <Typography variant="body1">Do you want to continue ?</Typography>
      </CustomModal>
    </>
  );
};

export default DeleteStaffGroup;
